import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import { createStyles, Theme } from "@material-ui/core/styles";
import { Box, Typography, Button, useMediaQuery, withStyles } from "@material-ui/core";
import { cloud } from "./assets";
import CustomButton from "../../../components/src/CustomButton.web";

interface Props {
  files: any[];
  postType?: string;
  setFiles: (files: any[]) => void;
  classes: any;
}

const interFF = {
  fontFamily: "Inter"
}

const styles = (theme: Theme) =>
  createStyles({
    container: {
      display: "flex",
      width: "100%",
      marginTop: "20px",
    },

    dropzone: {
      display: "flex",
      flexGrow: 1,
      flexDirection: "column",
      alignItems: "center",
      padding: "18px 25px 25px",
      gap: "20px",
      borderRadius: "10px",
      border: "2px dashed #ABB0BC",

      [theme.breakpoints.down(650)]: {
        padding: "15px",
        maxWidth: "100%",
      },
    },

    dropzoneInnerContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      paddingTop: "4px",
    },

    fileDescriptionContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      paddingTop: "10px",
      paddingBottom: "22px",

      "& > p": {
        ...interFF,
      },
    },

    dropzoneTitle: {
      fontSize: "24px",
      fontWeight: 700,
      lineHeight: "32px",
      letterSpacing: "-0.12px",
      ...interFF,

      [theme.breakpoints.down(650)]: {
        fontSize: "16px",
      },
    },

    dropzoneText: {
      fontSize: "14px",
      lineHeight: "20px",
      ...interFF,

      [theme.breakpoints.down(650)]: {
        fontSize: "13px",
        textAlign: "center",
      },
    },

    limitsText: {
      fontSize: "14px",
      lineHeight: "25px",
      fontWeight: 700,

      [theme.breakpoints.down(650)]: {
        fontSize: "12px",
      },
    },

    button: {
      width: "360px",
      height: "56px",

      [theme.breakpoints.down(650)]: {
        width: "240px",
        fontSize: "14px",
      },
    },

    cloudIcon: {
      width: "72px",
      height: "72px",

      [theme.breakpoints.down(768)]: {
        width: "50px",
        height: "50px",
      },
    },

    mobilePicker: {
      display: "flex",
      flexGrow: 1,
      flexDirection: "column",
      alignItems: "center",
      padding: "30px 15px",
      gap: "16px",
      borderRadius: "10px",
      border: "2px dashed #ABB0BC",
    },
  });

const FileDropzone: React.FC<Props> = ({ files, setFiles, classes, postType }) => {
  const isMobile = useMediaQuery('(max-width:650px)');
  const [error, setError] = useState<string | null>(null);
  const dropzoneOptions = {
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".heic", ".webp"],
      "video/*": [".mp4", ".MP4", ".mov", ".MOV", ".m4v", ".M4V", ".hevc"],
    },
    maxSize: 500 * 1024 * 1024, // 500 MB
  
    onDrop: (acceptedFiles: any[]) => {
      setFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      );
    },
  
    onDropRejected: (fileRejections: any[]) => {
      const error = fileRejections[0]?.errors[0];
  
      if (error?.code === "file-too-large") {
        setError("File size must be less than 500 MB.");
      } else {
        setError(error?.message || "Invalid file.");
      }
    },
  
  };

  const { getRootProps, getInputProps } = useDropzone(dropzoneOptions);

  const { getInputProps: getUploadInputProps, open: openUpload } = useDropzone({
    ...dropzoneOptions,
    noClick: true,
    noKeyboard: true,
    noDrag: true,
  });

  if (postType === "post" && isMobile) {
    return (
      <section className={classes.container}>
        <Box className={classes.mobilePicker}>
          <input {...getUploadInputProps({ accept: "image/*, video/*" })} id="uploadInput" />
          <img className={classes.cloudIcon} src={cloud} />
          <Box className={classes.dropzoneInnerContainer}>
            <Typography className={classes.dropzoneTitle}>
              Select video or photo to upload
            </Typography>
          </Box>
          <CustomButton
            className={classes.button}
            data-test-id="open-upload-picker"
            onClick={openUpload}
          >
            Upload
          </CustomButton>
        </Box>
      </section>
    );
  }

  return (
    <section className={classes.container}>
      <div {...getRootProps({ className: classes.dropzone })}>
        <input {...getInputProps({accept: "image/*, video/*", capture: "environment"})} id="fileInput"/>
        <img
          className={classes.cloudIcon}
          src={cloud}
          // style={{ width: 72, height: 72, color: "#E0E0E0" }}
        />
        <Box className={classes.dropzoneInnerContainer}>
          <Typography className={classes.dropzoneTitle}>
            {postType === "post" ? "Select video or photo to upload" : "Select video to upload"}
          </Typography>
          <Typography className={classes.dropzoneText} style={{paddingTop: postType === "post" ? "30px" : "0"}}>
            or drag and drop a image file
          </Typography>
        </Box>

        <Box className={classes.fileDescriptionContainer}>
          <Box hidden={postType !== "post"}>
            <Typography className={classes.limitsText}>JPG, PNG, MP4 or MOV</Typography>
          </Box>
          <Typography className={classes.limitsText}>
            720X1280 resolution or higher
          </Typography>
          {postType !== "post" && <Typography className={classes.limitsText}>Up to 1 minute</Typography>}
          <Typography className={classes.limitsText}>{postType === "post" ? "Less than 500 MB" : "Less than 500 MB"}</Typography>
        </Box>
        {error && (
          <Typography
            style={{
              color: "#D32F2F",
              fontSize: "14px",
              marginTop: "12px",
              textAlign: "center",
            }}
          >
            {error}
          </Typography>
        )}

        <CustomButton className={classes.button}>
          Select File
        </CustomButton>
      </div>
    </section>
  );
};

export default withStyles(styles)(FileDropzone);
